import { keepPreviousData, Query, useQuery } from '@tanstack/react-query';
import { useContext, useRef } from 'react';
import useGlynt from './useGlynt';
import { AppContext } from '../../App';
import useExtractionBatchSearchParams from './useExtractionBatchSearchParams';
import { localizeDate } from '../../utils/localizeDate';
import { EB_POLL_BASE_MS, EB_POLL_MAX_MS } from '../../constants';
import { IExtractionBatchListResponse } from '../../api/types';
import { IExtractionBatch, Statuses } from '../../types/my-types';

type ParamTypes = {
  limit: number;
  defaultPage?: number;
  labelContains?: string;
  ebId?: string;
  documentId?: string;
  verified?: string;
  extractionId?: string;
  trainingSetLabel?: string;
  createdAt?: string;
  hasUndismissedValidationErrors?: string;
  trainingSetCertified?: string;
};

type PollBackoff = {
  queryHash: string;
  dataUpdateCount: number;
  fingerprint: string;
  attempt: number;
};

const isUnfinished = (batch: IExtractionBatch): boolean =>
  batch.status === Statuses.InProgress ||
  batch.status === Statuses.Pending ||
  !batch.finished ||
  batch.data_processing_status === Statuses.InProgress;

/** Identifies the unfinished rows and their state; empty when every row is finished. */
const unfinishedFingerprint = (batches: IExtractionBatch[] = []): string =>
  batches
    .filter(isUnfinished)
    .map((batch) => `${batch.id}:${batch.status}:${batch.data_processing_status}`)
    .sort()
    .join('|');

const pollIntervalMs = (attempt: number): number =>
  Math.min(EB_POLL_BASE_MS * 2 ** attempt, EB_POLL_MAX_MS);

export default function usePaginatedExtractionBatches({
  limit,
  defaultPage,
  labelContains,
  ebId,
  verified,
  documentId,
  extractionId,
  trainingSetLabel,
  createdAt = '',
  hasUndismissedValidationErrors,
  trainingSetCertified = '',
}: ParamTypes) {
  const backoff = useRef<PollBackoff>({
    queryHash: '',
    dataUpdateCount: -1,
    fingerprint: '',
    attempt: 0,
  });

  const { generateSearchParams } = useExtractionBatchSearchParams();
  const glynt = useGlynt();
  // refetch query when datapool id is changed
  const { dataPoolId } = useContext(AppContext);

  let localCreatedAtStartUTCString: any;
  let localCreatedAtEndUTCString: any;

  const searchParamsToUse = generateSearchParams(dataPoolId);

  if (createdAt !== '') {
    const dateResponse = localizeDate(createdAt);

    if (dateResponse?.start && dateResponse?.end) {
      localCreatedAtStartUTCString = dateResponse.start;
      localCreatedAtEndUTCString = dateResponse.end;
    }
  }

  const { data, isError, isPending, isFetching, refetch } = useQuery({
    queryKey: [
      'extractionBatches',
      searchParamsToUse,
      labelContains,
      ebId,
      documentId,
      verified,
      extractionId,
      trainingSetLabel,
      createdAt,
      hasUndismissedValidationErrors,
      trainingSetCertified,
    ],

    queryFn: async () => {
      return (await glynt).extractionBatch.list({
        advanced: true,
        ordering: '-created_at',
        limit,
        offset: searchParamsToUse.page * limit,
        'quick-view': 'true',
        // optional params
        ...(labelContains !== '' && { ilabel__contains: labelContains }),
        ...(ebId !== '' && { id: ebId }),
        ...(verified !== '' && { verified: verified === 'yes' ? 'true' : 'false' }),
        ...(documentId !== '' && { document: documentId }),
        ...(extractionId !== '' && { extraction: extractionId }),
        ...(trainingSetLabel !== '' && { training_set_label: trainingSetLabel }),
        ...(createdAt !== '' && { created_after: localCreatedAtStartUTCString }),
        ...(createdAt !== '' && { created_before: localCreatedAtEndUTCString }),
        ...(hasUndismissedValidationErrors !== '' && {
          validation_errors: hasUndismissedValidationErrors === 'yes' ? 'true' : 'false',
        }),
        ...(trainingSetCertified !== '' && {
          training_set_certified: trainingSetCertified === 'yes' ? 'true' : 'false',
        }),
      });
    },

    enabled: !!dataPoolId,
    refetchInterval: (query) => nextRefetchInterval(query),
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  });

  /**
   * Polls while the page shows unfinished rows, backing off on each poll that
   * returns them unchanged; a new query or any change to those rows restarts
   * at the base interval.
   */
  function nextRefetchInterval(
    query: Pick<Query<IExtractionBatchListResponse>, 'queryHash' | 'state'>
  ): number | false {
    const { queryHash } = query;
    const { dataUpdateCount } = query.state;
    const fingerprint = unfinishedFingerprint(query.state.data?.results);
    const previous = backoff.current;

    if (queryHash !== previous.queryHash || dataUpdateCount !== previous.dataUpdateCount) {
      const restart = queryHash !== previous.queryHash || fingerprint !== previous.fingerprint;
      backoff.current = {
        queryHash,
        dataUpdateCount,
        fingerprint,
        attempt: restart ? 0 : previous.attempt + 1,
      };
    }

    return fingerprint ? pollIntervalMs(backoff.current.attempt) : false;
  }

  return {
    data,
    isError,
    isPending,
    isLoading: isPending,
    isFetching,
    refetch,
  };
}
